import { useQuery } from '@tanstack/react-query';
import axios from 'axios';
import { GetAiModelDetailResponseData } from '@manifest-cyber/types/interface/contracts/model-analysis/get.contract';
import { ManifestApiResponse } from '@manifest-cyber/types/interface/apiResponse';
import { fetchModel } from '../../../api/models/models.api';

type ModelDetailResponse = ManifestApiResponse<GetAiModelDetailResponseData>;

export const useModelDetail = (modelId: string | undefined) => {
  const { data, isLoading, error, isError } = useQuery<ModelDetailResponse>(
    ['modelDetail', modelId],
    async () => {
      if (!modelId) {
        throw new Error('Model ID is required');
      }
      const modelData = await fetchModel(modelId);
      return {
        success: true,
        data: modelData,
        errors: [],
      } as ModelDetailResponse;
    },
    {
      enabled: !!modelId,
      staleTime: 5 * 60 * 1000,
    },
  );

  // Get modelData directly from the query result
  const modelData = data?.data || null;

  // Determine if there's a true error (not just a 404)
  const isRealError =
    isError &&
    (!axios.isAxiosError(error) ||
      (axios.isAxiosError(error) && error.response?.status !== 404));

  // Check if there's a controlled failure
  const isControlledFailure = data && !data.success;

  // Check if we should show an error state
  const shouldShowError =
    isRealError || isControlledFailure || (!modelData && !isLoading);

  return {
    modelData,
    isLoading,
    isError: shouldShowError,
  };
};
